import { type FC, useState, useEffect, useCallback, useRef } from 'react';
import { useIntl, defineMessages } from 'dibs-react-intl';
import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';
import { useFragment, useRelayEnvironment, graphql } from 'react-relay';
import classnames from 'classnames';
import { getBuyerId } from 'dibs-cookie-jar';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader.js';
import HeartFilled from 'dibs-icons/exports/legacy/HeartFilled.ts';
import { formatLikes } from './utils/formatLikes.ts';
import { addNftItemLike, removeNftItemLike } from './mutations/LikeItemMutation.ts';
import styles from './styles/HeartPill.scss';
import { type HeartPill_nftItem$key } from './__generated__/HeartPill_nftItem.graphql.ts';
import { type HeartPill_viewer$key } from './__generated__/HeartPill_viewer.graphql.ts';
import { useNftUserLike } from './useNftUserLike.ts';

const { EVENT_WISHLIST } = eventNameConstants;
const { INTERACTION_TYPE_ITEM_FAVORITING } = interactionTypeConstants;
const { STEP_INTERACTION_ITEM_ADDED, STEP_INTERACTION_ITEM_REMOVED } = stepInteractionConstants;

const messages = defineMessages({
    likesLabel: {
        id: 'nftHeartPill.likesLabel',
        defaultMessage: '{likeCount} Favorites. {stateText}',
    },
    labelFavorited: {
        id: 'nftHeartPill.likesLabel.favorited',
        defaultMessage: 'Favorited',
    },
    labelFavorite: {
        id: 'nftHeartPill.likesLabel.favorite',
        defaultMessage: 'Favorite',
    },
});

type Props = {
    visible: boolean;
    containerClass?: string;
    nftItem: HeartPill_nftItem$key | null | undefined;
    viewer: HeartPill_viewer$key | null | undefined;
    isVertical?: boolean;
    shouldFillHeartOnHover?: boolean;
    gaLabel?: string;
};

const handleUserAuth = async (gaLabel: string): Promise<string> => {
    const { LIKE_NFT_ITEM_FLOW } = authModalLoader.constants;
    const result = await authModalLoader.show({
        flow: LIKE_NFT_ITEM_FLOW,
        ga: {
            label: `save item - NFT ${gaLabel}`.trim(),
        },
    });
    return result?.userId ?? '';
};

export const HeartPill: FC<Props> = ({
    visible = true,
    containerClass = '',
    nftItem: nftItemRef,
    viewer: viewerRef,
    isVertical = false,
    shouldFillHeartOnHover = false,
    gaLabel = '',
}) => {
    const intl = useIntl();
    const environment = useRelayEnvironment();
    const [isAnimating, setIsAnimating] = useState(false);
    const userJustLoggedIn = useRef(false);
    const viewer = useFragment(
        graphql`
            fragment HeartPill_viewer on Viewer {
                id
            }
        `,
        viewerRef
    );
    const nftItem = useFragment(
        graphql`
            fragment HeartPill_nftItem on NftItem {
                id
                serviceId
                likes {
                    likeCount
                }
            }
        `,
        nftItemRef
    );

    const { id: nodeId, serviceId: itemId, likes } = nftItem || {};
    const { isRefetching, isNftItemLikedByUser, refetch, userLikeNodeId } = useNftUserLike(
        itemId || ''
    );
    const { likeCount } = likes || {};
    const { id: viewerNodeId } = viewer || {};

    const executeLikeMutation = useCallback(async (): Promise<void> => {
        const userId = getBuyerId(document.cookie);
        if (itemId && nodeId && userId && viewerNodeId && userLikeNodeId && !isRefetching) {
            setIsAnimating(true);

            const mutation = isNftItemLikedByUser ? removeNftItemLike : addNftItemLike;
            trackEvent({
                event: 'e_ga_event',
                category: 'item favoriting',
                action: isNftItemLikedByUser ? 'item removed' : 'item added',
                label: gaLabel,
                additional: { itemId },
                noninteraction: 0,
                eventName: EVENT_WISHLIST,
                interaction_type: INTERACTION_TYPE_ITEM_FAVORITING,
                step_interaction_name: isNftItemLikedByUser
                    ? STEP_INTERACTION_ITEM_REMOVED
                    : STEP_INTERACTION_ITEM_ADDED,
            });
            await mutation({
                environment,
                itemId,
                userId,
                likeCount: likeCount ?? 0,
                userLikeNodeId,
                nodeId,
                viewerNodeId,
            });
        }
    }, [
        itemId,
        nodeId,
        viewerNodeId,
        isRefetching,
        userLikeNodeId,
        isNftItemLikedByUser,
        gaLabel,
        environment,
        likeCount,
    ]);

    // once user logs in and we refetch like status
    useEffect(() => {
        if (userJustLoggedIn.current) {
            // if item has already been liked by user - do nothing (item tile favorite behavior)
            if (isNftItemLikedByUser) {
                setIsAnimating(true);
            } else {
                executeLikeMutation();
            }
            userJustLoggedIn.current = false;
        }
    }, [isNftItemLikedByUser, executeLikeMutation]);

    const handleClick = async (): Promise<void> => {
        let buyerId = getBuyerId(document.cookie);

        if (buyerId) {
            executeLikeMutation();
        } else {
            buyerId = await handleUserAuth(gaLabel);
            if (buyerId) {
                refetch();
            }
        }
    };

    if (!nftItem) {
        return null;
    }

    const iconClasses = classnames(styles.icon, {
        [styles.isAnimating]: isAnimating,
        [styles.isFilled]: isNftItemLikedByUser,
        [styles.shouldFillHeartOnHover]: shouldFillHeartOnHover,
    });

    const containerClasses = classnames(styles.container, {
        [styles.active]: isNftItemLikedByUser,
        [styles.visible]: visible || isNftItemLikedByUser,
        [styles.isVertical]: isVertical,
        [styles.shouldFillHeartOnHover]: shouldFillHeartOnHover,
        [containerClass]: !!containerClass,
    });

    return (
        // eslint-disable-next-line react/forbid-elements
        <button
            type="button"
            className={containerClasses}
            onClick={handleClick}
            onAnimationEnd={() => {
                setIsAnimating(false);
            }}
            aria-label={intl.formatMessage(messages.likesLabel, {
                likeCount,
                stateText: isNftItemLikedByUser
                    ? intl.formatMessage(messages.labelFavorited)
                    : intl.formatMessage(messages.labelFavorite),
            })}
        >
            <HeartFilled className={iconClasses} />
            <span className={styles.number}>{formatLikes(likeCount ?? 0)}</span>
        </button>
    );
};
